import React from 'react';
import {
  FaCheckCircle,
  FaCircle,
  FaClock,
  FaExclamationTriangle,
  FaBox,
  FaTruck,
  FaMapMarkerAlt,
  FaUser,
  FaShippingFast
} from 'react-icons/fa';

const DeliveryProgressBar = ({
  totalStatuses,
  completedStatuses,
  type = 1,
  showReject = false
}) => {
  // Check if order has progressed beyond pending
  const hasProgressedBeyondPending = completedStatuses.some(status =>
    status.status_name.toLowerCase() !== 'pending' &&
    status.status_name.toLowerCase() !== 'reject'
  );

  // Filter out reject status if order has progressed beyond pending (unless showReject is true)
  const filteredTotalStatuses = (hasProgressedBeyondPending && !showReject)
    ? totalStatuses.filter(status => status.toLowerCase() !== 'reject')
    : totalStatuses;

  // Create a mapping of status names to their positions in the filtered statuses array
  const statusPositions = filteredTotalStatuses.reduce((acc, status, index) => {
    acc[status.toLowerCase()] = index;
    return acc;
  }, {});

  // Create completed status set for quick lookup (excluding reject if progressed beyond pending)
  const filteredCompletedStatuses = hasProgressedBeyondPending
    ? completedStatuses.filter(status => status.status_name.toLowerCase() !== 'reject')
    : completedStatuses;

  const completedStatusSet = new Set(
    filteredCompletedStatuses.map(status => status.status_name.toLowerCase())
  );

  // Find the latest completed status
  const latestCompletedIndex = filteredCompletedStatuses.length > 0
    ? Math.max(
      ...filteredCompletedStatuses.map(status =>
        statusPositions[status.status_name.toLowerCase()] ?? -1
      ).filter(index => index >= 0)
    )
    : -1;

  // Count only unique completed statuses that are part of the displayed total statuses
  const relevantCompletedCount = [...new Set(
    filteredCompletedStatuses
      .map(s => s.status_name.toLowerCase())
      .filter(name => filteredTotalStatuses.some(ts => ts.toLowerCase() === name))
  )].length;

  // Get icon for each status
  const getStatusIcon = (status, isCompleted, isCurrent, isRejected) => {
    if (isRejected) return FaExclamationTriangle;
    if (isCompleted) return FaCheckCircle;
    if (isCurrent) return FaClock;

    // Custom icons based on status type
    const iconMap = {
      'pending': FaClock,
      'distributor': FaUser,
      'approved': FaCheckCircle,
      'processing': FaBox,
      'order placed': FaBox,
      'picked': FaBox,
      'left': FaTruck,
      'in-transit': FaShippingFast,
      'arrived': FaMapMarkerAlt,
      'out for delivery': FaTruck,
      'delivered': FaCheckCircle,
      'reject': FaExclamationTriangle
    };

    return iconMap[status.toLowerCase()] || FaCircle;
  };

  // Get color classes for each status
  const getStatusColors = (status, isCompleted, isCurrent, isRejected) => {
    if (isRejected) {
      return {
        icon: 'text-red-500',
        bg: 'bg-red-100 border-red-200',
        line: 'bg-red-300'
      };
    }

    if (isCompleted) {
      return {
        icon: 'text-green-500',
        bg: 'bg-green-100 border-green-200',
        line: 'bg-green-400'
      };
    }

    if (isCurrent) {
      return {
        icon: 'text-blue-500',
        bg: 'bg-blue-100 border-blue-200 animate-pulse',
        line: 'bg-blue-400'
      };
    }

    return {
      icon: 'text-gray-400',
      bg: 'bg-gray-100 border-gray-200',
      line: 'bg-gray-300'
    };
  };

  // Format timestamp
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };


  // Get completed status details
  const getCompletedStatusDetails = (statusName) => {
    return filteredCompletedStatuses.find(
      status => status.status_name.toLowerCase() === statusName.toLowerCase()
    );
  };

  return (
    <div className="max-w-md mx-0 bg-white rounded-xl shadow-md px-4 py-1">
      {/* Header */}
      <div className="mb-3">
        {/* <h2 className="text-xl font-bold text-gray-800 mb-2">
          Delivery Progress
        </h2> */}
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
            Completed
          </span>
          <span className="flex items-center">
            <div className="w-3 h-3 bg-blue-400 rounded-full mr-2"></div>
            Current
          </span>
          <span className="flex items-center">
            <div className="w-3 h-3 bg-gray-300 rounded-full mr-2"></div>
            Pending
          </span>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="relative">
        {filteredTotalStatuses.map((status, index) => {
          const statusName = status.toLowerCase();
          const isCompleted = completedStatusSet.has(statusName);
          const isCurrent = index === latestCompletedIndex + 1 && !isCompleted;
          const isRejected = statusName === 'reject' && isCompleted;
          const isLast = index === filteredTotalStatuses.length - 1;

          const IconComponent = getStatusIcon(status, isCompleted, isCurrent, isRejected);
          const colors = getStatusColors(status, isCompleted, isCurrent, isRejected);
          const statusDetails = getCompletedStatusDetails(status);

          return (
            <div key={index} className="relative flex items-start">
              {/* Connecting Line */}
              {!isLast && (
                <div className="absolute left-6 top-12 w-0.5 h-14 bg-gray-200">
                  <div
                    className={`w-full transition-all duration-500 ${isCompleted ? colors.line : 'bg-gray-200'
                      }`}
                    style={{
                      height: isCompleted ? '100%' : '0%',
                      transition: 'height 0.5s ease-in-out'
                    }}
                  />
                </div>
              )}

              {/* Status Node */}
              <div className="flex items-start space-x-4 relative z-10">
                {/* Icon Circle */}
                <div
                  className={`
                    flex items-center justify-center w-12 h-12 rounded-full border-2 
                    transition-all duration-300 ${colors.bg}
                  `}
                >
                  <IconComponent className={`w-6 h-6 ${colors.icon}`} />
                </div>

                {/* Status Content */}
                <div className="flex-1 min-w-0 pb-3">
                  <div className="flex items-center justify-between">
                    <h3 className={`
                      text-sm font-semibold capitalize transition-colors duration-300
                      ${isCompleted || isCurrent ? 'text-gray-800' : 'text-gray-500'}
                    `}>
                      {status.replace(/[-_]/g, ' ')}
                    </h3>

                    {isCompleted && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                        ✓ Done
                      </span>
                    )}

                    {isCurrent && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full animate-pulse">
                        ⏳ In Progress
                      </span>
                    )}
                  </div>

                  {/* Status Details */}
                  {statusDetails && (
                    <div className="mt-1 space-y-1">
                      {/* {JSON.stringify(statusDetails)} */}
                      <p className="text-sm text-gray-600">
                        {formatDate(statusDetails.created_at)}
                      </p>
                      {statusDetails.created_name && (
                        <p className="text-xs text-gray-500">
                          by {statusDetails.created_name}
                        </p>
                      )}
                      {statusDetails.status_change_at && (
                        <p className="text-xs text-gray-500 capitalize">
                          via {statusDetails.status_change_at}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Placeholder for future steps */}
                  {!isCompleted && !isCurrent && (
                    <p className="text-xs text-gray-400 mt-1">
                      Waiting for previous step to complete
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress Summary */}
      <div className="mt-3 pt-2 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Progress</span>
          <span className="text-sm font-medium text-gray-800">
            {relevantCompletedCount} of {filteredTotalStatuses.length} steps
          </span>
        </div>

        {/* Progress Bar */}
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-green-400 to-blue-500 h-2 rounded-full transition-all duration-500"
            style={{
              width: `${Math.min((relevantCompletedCount / filteredTotalStatuses.length) * 100, 100)}%`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DeliveryProgressBar;
